import { useState, useEffect } from "react";
import { accountService } from "../../services/enhancedCrudService";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

const AccountForgotPin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpText, setOtpText] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [formData, setFormData] = useState({
    mobile_number: "",
    new_pin: "",
    confirm_pin: "",
    verification_token: "",
  });

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((timer) => timer - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.new_pin !== formData.confirm_pin) {
      toast.error("New PIN and confirm PIN do not match");
      return;
    }
    if (formData.new_pin.length < 4) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        verification_token: formData.verification_token,
        new_pin: formData.new_pin,
      };
      await accountService.forgotPin(payload);
      toast.success("PIN successfully reset!");
      setTimeout(() => {
        navigate("/login");
      }, 1000);
    } catch (error) {
      let message = "Failed to change PIN. Please try again.";
      if (error.response && error.response.data) {
        message =
          error.response.data.non_field_errors.length > 0
            ? error.response.data.non_field_errors[0]
            : message;
      }
      toast.error(message || "Failed to change PIN. Please try again.");
      console.error("Change PIN error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await accountService.verifyOtp({
        mobile_number: "(+91)" + formData.mobile_number,
        otp_code: otpText,
      });
      if (response.verification_token) {
        toast.success(response.message || "OTP verified successfully.");
        setFormData((prev) => ({
          ...prev,
          verification_token: response.verification_token,
        }));
      } else {
        toast.error(response.message || "OTP verification failed.");
      }
    } catch (error) {
      console.error("Verification error:", error);
      toast.error("OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await accountService.requestOtp({
        mobile_number: "(+91)" + formData.mobile_number,
        isForget: "true",
      });
      if (response.otp_code) {
        setOtpCode(response.otp_code);
        setCanResend(false);
        setResendTimer(60); // 60 seconds cooldown
        toast.success("OTP sent successfully!");
      } else {
        toast.error(response.message || "OTP sending failed.");
      }
    } catch (error) {
      console.error("OTP sending error:", error);
      toast.error("OTP sending failed.");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async (e) => {
    e.preventDefault();
    if (!canResend) return;
    
    try {
      setLoading(true);
      const response = await accountService.requestOtp({
        mobile_number: "(+91)" + formData.mobile_number,
        isForget: "true",
      });
      if (response.otp_code) {
        setOtpCode(response.otp_code);
        setCanResend(false);
        setResendTimer(60); // 60 seconds cooldown
        toast.success("OTP resent successfully!");
      } else {
        toast.error(response.message || "OTP resending failed.");
      }
    } catch (error) {
      console.error("OTP resending error:", error);
      toast.error("OTP resending failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="col-md-6 col-lg-4">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Forgot PIN</h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-7">
                  <div className="form-group mb-3 position-relative">
                    <label className="form-label">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className="form-control mobile-number-input input-form-input"
                        name="mobile_number"
                        value={formData.mobile_number}
                        onChange={handleInputChange}
                        disabled={formData.verification_token}
                        placeholder="Enter mobile number"
                        maxLength="10"
                        pattern="[0-9]{10}"
                        required
                      />
                      <span className="span-91">+91</span>
                  </div>
                </div>
                {!formData.verification_token && otpCode && (
                  <div className="col-md-5 pe-0">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        OTP Code <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control input-form-input"
                          name="otp_code"
                          value={otpText}
                          onChange={(e) => setOtpText(e.target.value)}
                          placeholder="Enter OTP code"
                          maxLength="6"
                          required
                        />
                      </div>
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={resendOtp}
                          className="btn btn-link p-0 text-decoration-none"
                          // disabled={!canResend || loading}
                          style={{ fontSize: '12px' }}
                        >
                          {canResend ? 'Resend OTP' : `Resend OTP in ${resendTimer}s`}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-md-5">
                  <div className="form-group mb-3">
                    <label className="form-label">&nbsp;</label>
                    {formData.verification_token ? (
                      <span className="text-success mt-2 d-block">
                        Verified
                      </span>
                    ) : otpCode ? (
                      <button
                        type="button"
                        onClick={handleVerify}
                        className="btn w-100 login-button"
                        disabled={loading || !otpText || otpText.length !== 6}
                        style={{
                          backgroundColor: loading || !otpText || otpText.length !== 6 ? "#ccc" : "",
                          cursor: loading || !otpText || otpText.length !== 6 ? "not-allowed" : "pointer",
                        }}
                      >
                        {loading ? "Verifying..." : "Verify"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={sendOtp}
                        className="btn w-100 login-button"
                      >
                        Send OTP
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {formData.verification_token && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        New PIN <span className="text-danger">*</span>
                      </label>
                      <input
                        type="password"
                        className="form-control"
                        name="new_pin"
                        value={formData.new_pin}
                        onChange={handleInputChange}
                        placeholder="Enter your New PIN"
                        maxLength="4"
                        pattern="[0-9]{4}"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Confirm PIN <span className="text-danger">*</span>
                      </label>
                      <input
                        type="password"
                        className="form-control"
                        name="confirm_pin"
                        value={formData.confirm_pin}
                        onChange={handleInputChange}
                        placeholder="Confirm your New PIN"
                        maxLength="4"
                        pattern="[0-9]{4}"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}
              {formData.verification_token && (
                <button
                  type="submit"
                  className="btn btn-success btn-lg w-100"
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Reset PIN"}
                </button>
              )}
            </form>

            <div className="text-center mt-3">
              <a href="/login">Back to Login</a>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default AccountForgotPin;
